import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteFilledIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ProductWishlistDialog from '../../../components/ProductWishlistDialog';
import {
  getInventoryItems,
  getInventoryItemDetails,
  getAllInventory,
  getLowStockItems,
  getOutOfStockItems,
  adjustInventory,
  getProductTransactionHistory,
  getInventoryAlerts,
  acknowledgeAlert,
  reverseTransaction,
  getInventoryStatistics,
  getLowStockReport,
  getOverstockReport,
  getOutOfStockReport,
  getBestSellers,
  getInventoryByFilters,
} from '../services/inventoryService';

// ✅ Import Redux actions for caching
import { fetchProductCategories } from '../../../app/store/slices/productCategorySlice';
import { fetchSuppliers } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands } from '../../../app/store/slices/productBrandSlice';

const InventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // ✅ Get cached data from Redux store
  const { categories: categoriesFromStore, loading: categoriesLoading } = useSelector(
    state => state.productCategories || { categories: [], loading: false }
  );
  const { suppliers: suppliersFromStore, loading: suppliersLoading } = useSelector(
    state => state.suppliers || { suppliers: [], loading: false }
  );
  const { brands: brandsFromStore, loading: brandsLoading } = useSelector(
    state => state.productBrands || { brands: [], loading: false }
  );

  // State Management
  const [inventory, setInventory] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1); // 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  // ✅ Removed activeTab - Stock Overview now only shows All Items
  // const [activeTab, setActiveTab] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  // Dialog States
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [wishlistProduct, setWishlistProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(20);
  const [transactionTotalItems, setTransactionTotalItems] = useState(0);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    reason: '',
    type: 'increase', // 'increase' or 'decrease'
  });

  // NEW: Category, Brand, and Supplier filter state
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  const [actionMenu, setActionMenu] = useState({ anchor: null, item: null });
  
  // ✅ Prevent duplicate API calls
  const [isFetching, setIsFetching] = useState(false);
  
  // ✅ Use data from Redux store (no local state needed)
  const categories = categoriesFromStore;
  const brands = brandsFromStore;
  const suppliers = suppliersFromStore;

  useEffect(() => {
    fetchAlerts();
    
    // ✅ Load categories, suppliers, brands from Redux store (cached)
    // Check if data exists in store, if not fetch from API
    if (categoriesFromStore.length === 0 && !categoriesLoading) {
      dispatch(fetchProductCategories());
    }
    
    if (suppliersFromStore.length === 0 && !suppliersLoading) {
      dispatch(fetchSuppliers());
    }
    
    if (brandsFromStore.length === 0 && !brandsLoading) {
      dispatch(fetchProductBrands());
    }
  }, []); // Only run once on mount

  useEffect(() => {
    fetchInventoryData();
  }, [searchQuery, page, rowsPerPage, sortBy, sortOrder, dateRange]);

  const fetchInventoryData = async () => {
    // Prevent duplicate concurrent calls
    if (isFetching) return;
    
    setIsFetching(true);
    try {
      setLoading(true);
      
      // ✅ Stock Overview now only shows All Items
      // Other views (Low Stock, Overstock, Best Sellers, Out of Stock) are separate pages
      const params = {
        searchTerm: searchQuery.trim() || undefined,
        filter: 'all',
        page: page,
        pageSize: rowsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };
        
      const response = await getInventoryItems(params);
      const data = response?.payload || response?.data || response || {};
      
      // Extract dashboard stats from response (if available)
      if (data.DashboardStats) {
        const dashStats = data.DashboardStats;
        const mappedStats = {
          totalProducts: dashStats.TotalProducts || 0,
          inStockProducts: dashStats.InStockProducts || 0,
          lowStockItems: dashStats.LowStockItems || 0,
          outOfStock: dashStats.OutOfStock || 0,
          totalStockValue: dashStats.TotalStockValue || 0,
          totalInventoryQuantity: dashStats.TotalInventoryQuantity || 0,
        };
        setDashboardStats(mappedStats);
      }
      
      // Extract items from nested structure
      if (data.Items) {
        const items = data.Items.Items || [];
        const totalCount = data.Items.TotalItems || 0;
        setInventory(items);
        setTotalItems(totalCount);
      } else {
        setInventory([]);
        setTotalItems(0);
      }
    } catch (error) {
      toast.error('Failed to load inventory data');
      setInventory([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await getInventoryAlerts();
      const data = response?.payload || response?.data || response || {};
      // Handle paginated alerts response
      const alertsData = data.Items || [];
      setAlerts(alertsData.filter(alert => !alert.IsAcknowledged));
    } catch (error) {
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchInventoryData(),
        fetchAlerts(),
      ]);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustInventory = (product) => {
    setSelectedProduct(product);
    setAdjustmentData({
      quantity: 0,
      reason: '',
      type: 'increase',
    });
    setAdjustDialogOpen(true);
  };

  const handleSaveAdjustment = async () => {
    try {
      if (!adjustmentData.quantity || adjustmentData.quantity <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }

      if (!adjustmentData.reason.trim()) {
        toast.error('Please provide a reason for adjustment');
        return;
      }

      // Match backend InventoryAdjustmentDto
      const adjustment = {
        ProductId: selectedProduct.ProductId,
        QuantityChange: adjustmentData.type === 'increase' 
          ? Math.abs(adjustmentData.quantity) 
          : -Math.abs(adjustmentData.quantity),
        Reason: adjustmentData.reason,
        ReferenceNumber: null, // Optional
      };

      await adjustInventory(adjustment);
      toast.success('Inventory adjusted successfully');
      setAdjustDialogOpen(false);
      fetchInventoryData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to adjust inventory');
    }
  };

  const handleViewHistory = async (product, page = 1) => {
    try {
      setSelectedProduct(product);
      const response = await getProductTransactionHistory(product.ProductId, page, transactionPageSize);
      const data = response?.payload || response?.data || response || {};
      
      // Handle paginated response
      if (data.Items) {
        setTransactionHistory(data.Items || []);
        setTransactionTotalItems(data.TotalItems || 0);
        setTransactionPage(page);
      } else {
        // Fallback for non-paginated response
        setTransactionHistory(data || []);
        setTransactionTotalItems(data.length || 0);
      }
      
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load transaction history');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleReverseTransaction = async (transactionId) => {
    if (window.confirm('Are you sure you want to reverse this transaction?')) {
      try {
        await reverseTransaction(transactionId);
        toast.success('Transaction reversed successfully');
        handleViewHistory(selectedProduct);
        fetchInventoryData();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reverse transaction');
      }
    }
  };

  const getStockStatusColor = (item) => {
    const status = item.Status || item.StockLevel;
    if (status === 'Out' || status === 'OutOfStock' || item.CurrentStock === 0) return 'error';
    if (status === 'Low' || status === 'Critical' || status === 'LowStock' || item.CurrentStock <= item.MinStock) return 'warning';
    return 'success';
  };

  const getStockStatusText = (item) => {
    const status = item.Status || item.StockLevel;
    if (status === 'Out' || status === 'OutOfStock' || item.CurrentStock === 0) return 'Out of Stock';
    if (status === 'Low' || status === 'LowStock') return 'Low Stock';
    if (status === 'Critical') return 'Critical';
    return 'In Stock';
  };

  const calculateStockPercentage = (item) => {
    if (!item.MaxStock || item.MaxStock === 0) return 100;
    return (item.CurrentStock / item.MaxStock) * 100;
  };

  // NEW: Handle category/supplier/brand filter button click
  const handleApplyFilter = async () => {
    if (!selectedCategoryId && !selectedSupplierId && !selectedBrandId) {
      toast.warning(t('please_select_filter') || 'Please select at least one filter (Category, Brand, or Supplier)');
      return;
    }

    // Prevent duplicate calls
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    try {
      
      const response = await getInventoryByFilters(
        selectedCategoryId || null,
        selectedSupplierId || null,
        selectedBrandId || null
      );
      
      
      // Handle response structure
      const inventoryData = response?.payload || response || [];
      const mappedInventory = Array.isArray(inventoryData) ? inventoryData.map(item => ({
        ProductId: item.ProductId || item.Id,
        ProductName: item.ProductName || item.Name,
        SKU: item.ProductSKU || item.SKU,
        CurrentStock: item.CurrentStock || item.StockQuantity || 0,
        MinStock: item.MinStockLevel || 0,
        MaxStock: item.MaxStockLevel || null,
        StockLevel: item.StockLevel || 'Normal',
        Status: item.Status || 'Active',
        LastUpdated: item.LastUpdated || item.ModifiedDate,
        CostPrice: item.CostPrice || 0,
        SalePrice: item.SalePrice || 0,
        InventoryValue: (item.CurrentStock || 0) * (item.CostPrice || 0),
        CategoryName: item.CategoryName,
        SupplierName: item.SupplierName,
      })) : [];
      
      setInventory(mappedInventory);
      setTotalItems(mappedInventory.length);
      setFilterApplied(true);
      setPage(1);
      // ✅ Removed setActiveTab since tabs are removed
      
      toast.success(t('filter_applied') || `Found ${mappedInventory.length} items`);
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to apply filter');
      setInventory([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InventoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('inventory_management') || 'Inventory Management'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('track_and_manage_stock') || 'Track and manage your product stock levels'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
          sx={{ borderRadius: 2 }}
        >
          {t('refresh') || 'Refresh'}
        </Button>
      </Box>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => alerts.forEach(alert => handleAcknowledgeAlert(alert.Id))}
            >
              Acknowledge All
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
          </Typography>
          {alerts.slice(0, 3).map(alert => (
            <Typography key={alert.Id} variant="caption" display="block">
              • [{alert.Severity}] {alert.Message}
            </Typography>
          ))}
          {alerts.length > 3 && (
            <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
              ... and {alerts.length - 3} more
            </Typography>
          )}
        </Alert>
      )}

      {/* Dashboard Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main', flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Products
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {dashboardStats?.totalProducts || 0}
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main', flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Low Stock Items
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {dashboardStats?.lowStockItems || 0}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid', borderColor: 'error.main', flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Out of Stock
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {dashboardStats?.outOfStock || 0}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main', flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Stock Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    €{dashboardStats?.totalStockValue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          {t('filters') || 'Filters'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('search_product') || 'Search by product name or SKU...'}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('category') || 'Category'}</InputLabel>
              <Select
                value={selectedCategoryId}
                label={t('category') || 'Category'}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <MenuItem value=""><em>{t('all_categories') || 'All Categories'}</em></MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.Id} value={category.Id}>{category.CategoryName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('brand') || 'Brand'}</InputLabel>
              <Select
                value={selectedBrandId}
                label={t('brand') || 'Brand'}
                onChange={(e) => setSelectedBrandId(e.target.value)}
              >
                <MenuItem value=""><em>{t('all_brands') || 'All Brands'}</em></MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.Id} value={brand.Id}>{brand.BrandName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('supplier') || 'Supplier'}</InputLabel>
              <Select
                value={selectedSupplierId}
                label={t('supplier') || 'Supplier'}
                onChange={(e) => setSelectedSupplierId(e.target.value)}
              >
                <MenuItem value=""><em>{t('all_suppliers') || 'All Suppliers'}</em></MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.Id} value={supplier.Id}>{supplier.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* ✅ Date range filter removed - was only for Best Sellers tab (now a separate page) */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={handleApplyFilter}
              disabled={!selectedCategoryId && !selectedSupplierId && !selectedBrandId}
              sx={{ height: 40, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {t('apply_filter') || 'Apply Filter'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={() => {
                setSelectedCategoryId('');
                setSelectedBrandId('');
                setSelectedSupplierId('');
                setFilterApplied(false);
                setPage(1);
                fetchInventoryData();
              }}
              disabled={!selectedCategoryId && !selectedSupplierId && !selectedBrandId && !filterApplied}
              sx={{ height: 40, borderRadius: 2, textTransform: 'none' }}
            >
              {t('clear') || 'Clear'}
            </Button>
          </Grid>
        </Grid>
        {filterApplied && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('filter_active') || 'Filter is active'} -
            {selectedCategoryId && ` ${t('category')}: ${categories.find(c => c.Id === selectedCategoryId)?.CategoryName}`}
            {selectedCategoryId && selectedBrandId && ' | '}
            {selectedBrandId && ` ${t('brand') || 'Brand'}: ${brands.find(b => b.Id === selectedBrandId)?.BrandName}`}
            {(selectedCategoryId || selectedBrandId) && selectedSupplierId && ' | '}
            {selectedSupplierId && ` ${t('supplier')}: ${suppliers.find(s => s.Id === selectedSupplierId)?.Name}`}
          </Alert>
        )}
      </Paper>

      {/* Inventory Table - All Items */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Current Stock</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Min Stock</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Sale Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Inventory Value</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No inventory items found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventory
                  .slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={item.Id || item.ProductId} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      {/* Product Name */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Copy name">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(item.ProductName)} sx={{ opacity: 0.35, '&:hover': { opacity: 1 }, p: 0.3 }}>
                              <ContentCopyIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.ProductName}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {/* SKU */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {item.SKU || '-'}
                          </Typography>
                          {item.SKU && (
                            <Tooltip title="Copy SKU">
                              <IconButton size="small" onClick={() => navigator.clipboard.writeText(item.SKU)} sx={{ opacity: 0.35, '&:hover': { opacity: 1 }, p: 0.3 }}>
                                <ContentCopyIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      
                      {/* Current Stock */}
                      <TableCell align="center">
                        <Chip
                          label={item.CurrentStock}
                          size="small"
                          color={getStockStatusColor(item)}
                          sx={{ fontWeight: 600, minWidth: 60 }}
                        />
                      </TableCell>
                      
                      {/* Min Stock */}
                      <TableCell align="center">
                        <Typography variant="body2">{item.MinStock}</Typography>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={getStockStatusText(item)}
                          size="small"
                          color={getStockStatusColor(item)}
                          icon={
                            item.CurrentStock === 0 ? <ErrorIcon /> :
                            item.CurrentStock <= item.MinStock ? <WarningIcon /> :
                            <CheckCircleIcon />
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      
                      {/* Cost Price */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          €{item.CostPrice?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      
                      {/* Sale Price */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          €{item.SalePrice?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      
                      {/* Inventory Value */}
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          €{item.InventoryValue?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      
                      {/* Actions */}
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => setActionMenu({ anchor: e.currentTarget, item })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1} // MUI uses 0-based index
          onPageChange={(e, newPage) => setPage(newPage + 1)} // Convert back to 1-based
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(1); // Reset to first page
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Adjust Inventory Dialog */}
      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Adjust Inventory - {selectedProduct?.ProductName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={adjustmentData.type}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, type: e.target.value })}
                label="Adjustment Type"
              >
                <MenuItem value="increase">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon color="success" />
                    Increase Stock
                  </Box>
                </MenuItem>
                <MenuItem value="decrease">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon color="error" />
                    Decrease Stock
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={adjustmentData.quantity}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: parseInt(e.target.value) || 0 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {adjustmentData.type === 'increase' ? <AddIcon color="success" /> : <RemoveIcon color="error" />}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Adjustment"
              value={adjustmentData.reason}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
              placeholder="e.g., Damaged goods, Stock count correction, etc."
            />

            <Alert severity="info">
              Current Stock: <strong>{selectedProduct?.CurrentStock}</strong> units
              <br />
              New Stock: <strong>
                {adjustmentData.type === 'increase'
                  ? (selectedProduct?.CurrentStock || 0) + (adjustmentData.quantity || 0)
                  : (selectedProduct?.CurrentStock || 0) - (adjustmentData.quantity || 0)}
              </strong> units
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAdjustment} variant="contained" color="primary">
            Save Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Transaction History - {selectedProduct?.ProductName}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Document</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Qty Change</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Previous</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">New Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Unit Cost</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total Value</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No transaction history available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactionHistory.map((transaction) => (
                    <TableRow key={transaction.Id} sx={{ bgcolor: transaction.IsReversed ? '#ffebee' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(transaction.TransactionDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {transaction.TransactionReference || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.TransactionType}
                          size="small"
                          color={
                            transaction.TransactionType?.includes('Sale') || 
                            transaction.TransactionType?.includes('Decrease') ? 'error' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {transaction.SourceDocumentNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: transaction.QuantityChange > 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {transaction.QuantityChange > 0 ? '+' : ''}{transaction.QuantityChange}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{transaction.PreviousQuantity}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {transaction.NewQuantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {transaction.UnitCost ? `€${transaction.UnitCost.toFixed(2)}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {transaction.TotalValue ? `€${transaction.TotalValue.toFixed(2)}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{transaction.Notes || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {transaction.IsReversed ? (
                          <Chip label="Reversed" size="small" color="error" />
                        ) : (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {transactionTotalItems > transactionPageSize && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <TablePagination
                component="div"
                count={transactionTotalItems}
                page={transactionPage - 1}
                onPageChange={(e, newPage) => handleViewHistory(selectedProduct, newPage + 1)}
                rowsPerPage={transactionPageSize}
                onRowsPerPageChange={(e) => {
                  setTransactionPageSize(parseInt(e.target.value, 10));
                  handleViewHistory(selectedProduct, 1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Add to Wishlist Dialog */}
      <ProductWishlistDialog
        open={Boolean(wishlistProduct)}
        onClose={() => setWishlistProduct(null)}
        product={wishlistProduct}
        onSuccess={() => {
          const addedId = wishlistProduct?.Id;
          if (addedId) {
            setInventory(prev => prev.map(i =>
              (i.ProductId || i.Id) === addedId ? { ...i, IsWishlistProduct: true } : i
            ));
          }
          setWishlistProduct(null);
        }}
      />

      {/* Row action context menu */}
      <Menu
        anchorEl={actionMenu.anchor}
        open={Boolean(actionMenu.anchor)}
        onClose={() => setActionMenu({ anchor: null, item: null })}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/inventory/${actionMenu.item?.ProductId || actionMenu.item?.Id}`);
            setActionMenu({ anchor: null, item: null });
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleAdjustInventory(actionMenu.item);
            setActionMenu({ anchor: null, item: null });
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
          Adjust Stock
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleViewHistory(actionMenu.item);
            setActionMenu({ anchor: null, item: null });
          }}
        >
          <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
          View History
        </MenuItem>
        <MenuItem
          disabled={actionMenu.item?.IsWishlistProduct}
          onClick={() => {
            if (!actionMenu.item?.IsWishlistProduct) {
              setWishlistProduct({
                Id: actionMenu.item?.ProductId || actionMenu.item?.Id,
                Name: actionMenu.item?.ProductName,
                SKU: actionMenu.item?.SKU,
                SalePrice: actionMenu.item?.SalePrice || actionMenu.item?.CostPrice || 0,
                CurrentStock: actionMenu.item?.CurrentStock,
                SupplierId: actionMenu.item?.SupplierId,
              });
            }
            setActionMenu({ anchor: null, item: null });
          }}
        >
          {actionMenu.item?.IsWishlistProduct
            ? <FavoriteFilledIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            : <FavoriteBorderIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          }
          {actionMenu.item?.IsWishlistProduct ? 'In Wishlist' : 'Add to Wishlist'}
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default InventoryPage;